import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthFacade, AuthorizationFacade } from '@elite/authentication/domain';
import { filter, firstValueFrom, map, merge, Subject, takeUntil, timeout } from 'rxjs';
import { RealtimeFacade } from '@elite/realtime/api';
import type { IncomingCallDto, SupportCallMedia } from '@elite/realtime/api';
import type { Room, RemoteTrack } from 'livekit-client';
import { environment } from '../../environments/environment';

export type IncomingCall = IncomingCallDto;
interface CallToken {
  url: string;
  token: string;
  roomName: string;
  identity: string;
}
interface CallState {
  roomName: string;
  status: string;
}
export interface CallHistory {
  callId: string;
  callerId: string;
  status: string;
  startedAt: string;
  durationSeconds: number;
}
type Envelope<T> = { success: boolean; result: T; errors?: string[] };

/** One media session across routes, using the shared realtime connection and availability queue. */
@Injectable({ providedIn: 'root' })
export class SupportCallsService implements SupportCallMedia {
  private readonly http = inject(HttpClient);
  private readonly realtime = inject(RealtimeFacade);
  private readonly sessionEnded$ = new Subject<void>();
  private readonly auth = inject(AuthFacade);
  private readonly destroy = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly permitted = toSignal(inject(AuthorizationFacade).hasPermissionById$('58000'), { initialValue: false });
  readonly signedIn = toSignal(this.auth.isLoggedIn$, { initialValue: false });
  readonly visible = computed(() => this.signedIn() && this.permitted());
  readonly available = toSignal(this.realtime.supportAvailable$, { initialValue: false });
  readonly changingAvailability = signal(false);
  readonly starting = signal(false);
  readonly canStartDriverCall = computed(
    () =>
      this.browser &&
      this.visible() &&
      this.connection() === 'connected' &&
      !this.active() &&
      !this.starting() &&
      !this.changingAvailability(),
  );

  readonly connection = toSignal(this.realtime.connectionState$, { initialValue: 'disconnected' });
  private readonly offered = toSignal(this.realtime.offeredCalls$, { initialValue: [] });
  readonly incoming = computed(() =>
    this.visible() && this.available() && !this.active() && !this.starting() ? this.offered() : [],
  );
  readonly active = signal<IncomingCall | null>(null);
  readonly status = signal('');
  readonly error = signal('');
  readonly muted = signal(false);
  readonly audioBlocked = signal(false);
  readonly seconds = signal(0);
  readonly history = signal<CallHistory[]>([]);
  private token = '';
  private owner = '';
  private room?: Room;
  private generation = 0;
  private session = 0;
  private claimed = false;
  private finishing = false;
  private connectDeadline?: ReturnType<typeof setTimeout>;
  private wanted = false;
  private polling = false;
  private timer?: ReturnType<typeof setInterval>;
  private ringTimer?: ReturnType<typeof setInterval>;
  private readonly audioElements = new Set<HTMLMediaElement>();
  private audioContext?: AudioContext;
  private connectedAt?: number;

  constructor() {
    effect(() => {
      if (!this.visible()) void this.stop();
    });
    effect(() => {
      if (this.incoming().length) this.startRing();
      else this.stopRing();
    });
    this.realtime.callTaken$.pipe(takeUntilDestroyed(this.destroy)).subscribe(({ callId, takenBy }) => {
      if (this.active()?.callId === callId && takenBy.trim().toLowerCase() !== this.owner.trim().toLowerCase()) {
        void this.end(false);
      }
    });
    this.realtime.availabilityRefused$.pipe(takeUntilDestroyed(this.destroy)).subscribe(({ reassert, message }) => {
      if (reassert) {
        this.wanted = false;
        this.error.set(message || 'Could not restore call availability.');
      }
    });
    this.realtime.connected$.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => {
      void this.reconcile();
    });
    this.auth.authentication$.pipe(takeUntilDestroyed(this.destroy)).subscribe((auth) => {
      const owner = auth.userClaims?.UserId ?? '';
      if (this.owner && owner !== this.owner) void this.stop();
      this.owner = owner;
      this.token = auth.token ?? '';
    });
    this.destroy.onDestroy(() => {
      void this.stop();
      void this.audioContext?.close();
    });
  }

  private async request<T>(command: string, body: object): Promise<T> {
    const origin = environment.apiUrl.replace(/\/+$/, '').replace(/\/api$/i, '');
    const response = await firstValueFrom(
      this.http.post<Envelope<T>>(`${origin}/api/Calls/Command/${command}`, body).pipe(timeout(15000)),
    );
    if (!response.success) throw new Error(response.errors?.join('\n') || 'The call request failed.');
    return response.result;
  }

  async setAvailable(value: boolean): Promise<boolean> {
    if (
      !this.browser ||
      !this.visible() ||
      this.changingAvailability() ||
      this.starting() ||
      this.active() ||
      this.finishing ||
      this.connection() !== 'connected'
    )
      return false;
    this.changingAvailability.set(true);
    this.error.set('');
    const session = this.session;
    const current = () => session === this.session && this.visible();
    try {
      if (value) {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('Voice calls need HTTPS and a microphone.');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        if (!current()) return false;
        this.audioContext ??= new AudioContext();
        await this.audioContext.resume();
        if (!current()) return false;
      }
      await this.setDuty(value);
      if (!current()) return false;
      this.wanted = value;
      if (value) {
        this.ensureTimer();
        await this.refreshHistory();
      }
      return true;
    } catch (e) {
      if (current()) this.error.set(this.message(e));
      return false;
    } finally {
      if (session === this.session) this.changingAvailability.set(false);
    }
  }

  /** Subscribe before dispatch so even an immediate refusal is observed. The effect remains the sole writer. */
  private async setDuty(available: boolean): Promise<void> {
    const settled = firstValueFrom(
      merge(
        this.realtime.availabilityConfirmed$.pipe(map((event) => ({ ...event, message: null, ok: true }))),
        this.realtime.availabilityRefused$.pipe(map((event) => ({ ...event, ok: false }))),
      ).pipe(
        filter((event) => !event.reassert && event.available === available),
        timeout(100000),
        takeUntil(this.sessionEnded$),
      ),
    );
    this.realtime.setAvailability(available);
    const result = await settled;
    if (!result.ok) throw new Error(result.message || 'Could not change call availability.');
  }

  dismiss(id: string): void {
    this.realtime.dismissCall(id);
  }

  async startDriverCall(driverId: string, displayName: string): Promise<boolean> {
    if (!driverId || this.finishing || !this.canStartDriverCall()) return false;
    const generation = ++this.generation;
    const session = this.session;
    this.starting.set(true);
    this.error.set('');
    const current = () => generation === this.generation && session === this.session && this.visible();
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Voice calls need HTTPS and a microphone.');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      if (!current()) return false;
      // Leave the inbound queue before ringing. Restore only the operator's previous duty preference.
      if (this.available()) await this.setDuty(false);
      if (!current()) return false;
      const token = await this.request<CallToken>('StartDriverCall', { driverId });
      const callId = token.roomName.slice('support:'.length);
      if (!current()) {
        if (session === this.session) await this.request('EndSupportCall', { callId });
        return false;
      }
      this.active.set({
        callId,
        roomName: token.roomName,
        callType: 'support',
        callerId: driverId,
        callerName: displayName.trim() || 'Driver',
      });
      this.claimed = true;
      this.status.set('Calling driver');
      this.connectDeadline = setTimeout(() => {
        if (generation === this.generation && !this.connectedAt) {
          this.error.set('The driver did not answer.');
          void this.end();
        }
      }, 30000);
      return await this.connectMedia(token, generation, 'Waiting for driver');
    } catch (e) {
      if (current()) {
        this.error.set(this.message(e));
        await this.end();
        if (this.wanted && session === this.session && !this.available()) {
          try {
            await this.setDuty(true);
          } catch {
            /* The shared availability queue reports failure. */
          }
        }
      }
      return false;
    } finally {
      if (session === this.session) this.starting.set(false);
    }
  }

  async accept(call: IncomingCall): Promise<boolean> {
    if (
      this.active() ||
      this.starting() ||
      this.finishing ||
      !this.visible() ||
      !this.available() ||
      this.connection() !== 'connected' ||
      !this.offered().some((offered) => offered.callId === call.callId && offered.roomName === call.roomName)
    )
      return false;
    const generation = ++this.generation;
    const session = this.session;
    this.active.set(call);
    this.status.set('Connecting');
    this.error.set('');
    this.claimed = false;
    this.dismiss(call.callId);
    let claimed = false;
    try {
      const token = await this.request<CallToken>('AcceptCall', { callId: call.callId });
      claimed = true;
      if (generation !== this.generation) {
        if (session === this.session) await this.request('EndSupportCall', { callId: call.callId });
        return false;
      }
      this.claimed = true;
      this.connectDeadline = setTimeout(() => {
        if (generation === this.generation && !this.connectedAt) {
          this.error.set('The caller could not connect.');
          void this.end();
        }
      }, 30000);
      await this.setDuty(false);
      return await this.connectMedia(token, generation, 'Waiting for caller');
    } catch (e) {
      if (generation === this.generation) {
        this.error.set(this.message(e));
        await this.end(claimed);
      }
      return false;
    }
  }

  private async connectMedia(token: CallToken, generation: number, waitingStatus: string): Promise<boolean> {
    const { Room, RoomEvent, Track, AudioPresets } = await import('livekit-client');
    if (generation !== this.generation) return false;
    // Prefer modern signalling; LiveKit retains negotiated fallback for older peers.
    const room = new Room({
      singlePeerConnection: true,
      publishDefaults: { audioPreset: AudioPresets.speech, dtx: true, red: true },
    });
    this.room = room;
    const connected = () => {
      if (generation !== this.generation) return;
      clearTimeout(this.connectDeadline);
      this.connectedAt ??= Date.now();
      this.status.set('On call');
    };
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio || generation !== this.generation) return;
      const element = track.attach();
      element.style.display = 'none';
      document.body.append(element);
      this.audioElements.add(element);
    });
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((element) => {
        element.remove();
        this.audioElements.delete(element);
      });
    });
    room.on(RoomEvent.ParticipantConnected, connected);
    room.on(RoomEvent.ParticipantDisconnected, () => {
      if (generation === this.generation) void this.end();
    });
    room.on(RoomEvent.Disconnected, () => {
      if (generation === this.generation) void this.end();
    });
    room.on(RoomEvent.Reconnecting, () => {
      if (generation === this.generation) this.status.set('Reconnecting');
    });
    room.on(RoomEvent.Reconnected, () => {
      if (generation !== this.generation) return;
      if (room.remoteParticipants.size) connected();
      else this.status.set(waitingStatus);
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (generation === this.generation) this.audioBlocked.set(!room.canPlaybackAudio);
    });
    await room.connect(token.url, token.token);
    if (generation !== this.generation) {
      await room.disconnect();
      return false;
    }
    await room.localParticipant.setMicrophoneEnabled(true);
    if (generation !== this.generation) {
      await room.disconnect();
      return false;
    }
    try {
      await room.startAudio();
    } catch {
      if (generation === this.generation) this.audioBlocked.set(true);
    }
    if (generation !== this.generation) return false;
    if (room.remoteParticipants.size) connected();
    else this.status.set(waitingStatus);
    this.ensureTimer();
    return true;
  }

  async setMuted(): Promise<void> {
    const room = this.room;
    const generation = this.generation;
    if (!room) return;
    try {
      const muted = !this.muted();
      await room.localParticipant.setMicrophoneEnabled(!muted);
      if (generation === this.generation) this.muted.set(muted);
    } catch (e) {
      if (generation === this.generation) this.error.set(this.message(e));
    }
  }
  async enableAudio(): Promise<void> {
    const room = this.room;
    const generation = this.generation;
    if (!room) return;
    try {
      await room.startAudio();
      if (generation === this.generation) this.audioBlocked.set(!room.canPlaybackAudio);
    } catch (e) {
      if (generation === this.generation) this.error.set(this.message(e));
    }
  }

  async end(notifyServer = true): Promise<void> {
    if (this.finishing) return;
    this.finishing = true;
    try {
      const call = this.active();
      const session = this.session;
      const claimed = this.claimed;
      this.claimed = false;
      clearTimeout(this.connectDeadline);
      ++this.generation;
      const room = this.room;
      this.room = undefined;
      this.active.set(null);
      this.status.set('');
      this.muted.set(false);
      this.audioBlocked.set(false);
      this.connectedAt = undefined;
      this.seconds.set(0);
      room?.removeAllListeners();
      try {
        await room?.disconnect();
      } catch {
        /* Local cleanup must finish even after transport loss. */
      }
      this.audioElements.forEach((element) => element.remove());
      this.audioElements.clear();
      if (call && notifyServer && claimed && session === this.session) {
        try {
          await this.request('EndSupportCall', { callId: call.callId });
        } catch (e) {
          this.error.set(this.message(e));
        }
      }
      if (session !== this.session) return;
      if (call && this.wanted && this.token && this.visible()) {
        try {
          await this.setDuty(true);
        } catch (e) {
          if (session === this.session) this.error.set(this.message(e));
        }
      }
      if (call && this.token) await this.refreshHistory();
    } finally {
      this.finishing = false;
    }
  }

  async refreshHistory(): Promise<void> {
    if (!this.browser || !this.visible()) return;
    const session = this.session;
    try {
      const history = (await this.request<{ calls: CallHistory[] }>('GetSupportCallHistory', {})).calls;
      if (session === this.session) this.history.set(history);
    } catch (e) {
      if (session === this.session) this.error.set(this.message(e));
    }
  }

  private ensureTimer(): void {
    this.timer ??= setInterval(() => {
      if (this.connectedAt) this.seconds.set(Math.floor((Date.now() - this.connectedAt) / 1000));
      if (!this.polling) void this.reconcile();
    }, 3000);
  }

  private async reconcile(): Promise<void> {
    if (this.polling) return;
    this.polling = true;
    const generation = this.generation;
    try {
      const active = this.active();
      if (active) {
        const state = await this.request<CallState>('GetCallState', { callId: active.callId });
        if (generation === this.generation && ['ended', 'missed'].includes(state.status)) await this.end(false);
      }
      for (const call of this.incoming()) {
        const state = await this.request<CallState>('GetCallState', { callId: call.callId });
        if (generation === this.generation && state.status !== 'ringing') this.dismiss(call.callId);
      }
    } catch {
      /* Keep media alive through a transient API outage. */
    } finally {
      this.polling = false;
    }
  }

  private startRing(): void {
    if (this.ringTimer) return;
    const play = () => {
      const audio = this.audioContext;
      if (!audio || audio.state !== 'running') return;
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = 440;
      gain.gain.value = 0.12;
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.4);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
    };
    play();
    this.ringTimer = setInterval(play, 1600);
  }
  private stopRing(): void {
    clearInterval(this.ringTimer);
    this.ringTimer = undefined;
  }
  private message(error: unknown): string {
    return error instanceof Error ? error.message : 'Unable to connect. Check your microphone and connection.';
  }
  private async stop(): Promise<void> {
    ++this.session;
    this.sessionEnded$.next();
    this.wanted = false;
    this.changingAvailability.set(false);
    this.starting.set(false);
    this.stopRing();
    clearInterval(this.timer);
    this.timer = undefined;
    this.history.set([]);
    this.error.set('');
    await this.end(false);
  }
}
